import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { LoadingIndicator, GeneralButton } from 'sharedComponents';
import { useWizardFlow } from '../../../hooks/useWizardFlow';
import { getWizardConfiguration } from '../../../config/wizardConfig';
import WizardStepper from '../WizardStepper/index.js';
import WizardStep from '../WizardStep/index.js';
import WizardActions from '../WizardActions/index.js';
import { validatePhysicalConnection } from '../../steps/PhysicalConnectionStep/utils/validationUtils';
import { UNMONITORED_GEARBOX_TYPE_CONFIG_ID, ASSET_TYPES } from '../../../constants';
import './WizardContainer.scss';
import logger from 'helpers/logger';

// Check if the Physical Connection step allows proceeding to the next step
const canProceedFromPhysicalConnection = (formData, assetDetails, errorSteps, translate) => {
    const physicalConnectionData = formData?.physicalConnection;

    if (physicalConnectionData?.isSkipped) {
        return true;
    }

    const physicalConnectionAssets = physicalConnectionData?.physicalConnection || [];
    const outputRatios = physicalConnectionData?.outputRatios || {};

    if (physicalConnectionAssets?.length === 0) {
        return false;
    }

    if (physicalConnectionAssets?.length > 0 && assetDetails?.length > 0) {
        try {
            const validationResult = validatePhysicalConnection(
                physicalConnectionAssets,
                assetDetails,
                outputRatios,
                translate
            );
            if (validationResult?.hasErrors || !validationResult?.isValid) {
                return false;
            }
        } catch (error) {
            logger.warn('Physical connection validation failed:', error);
            return false;
        }
    }

    if (errorSteps.includes(2)) {
        return false;
    }

    return true;
};

/**
 * WizardContainer - Main container component for the PowertrainWizard
 * Manages overall wizard state and coordinates between stepper, steps, and actions
 */
const WizardContainer = ({
    mode = 'create',
    existingModel = null,
    onComplete = () => { },
    onCancel = () => { },
    actions = null,
    sites = [],
    className = '',
    showDebug = false,
    // Redux state for assets
    availableAssets = [],
    isLoadingAssets = false,
    assetsError = null,
    // Redux state for asset subtypes
    assetSubTypes = [],
    isLoadingAssetSubTypes = false,
    assetSubTypesError = null,
    // Redux state for asset details
    assetDetails = [],
    isLoadingAssetDetails = false,
    assetDetailsError = null,
    // Redux state for powertrain operations
    isAddingPowertrain = false,
    isEditingPowertrain = false,
    powertrainOperationError = null,
    hierarchyData = null,
    organizationId = null,
    preSelectedAssetIds = []
}) => {
    const { t: translate } = useTranslation();
    const wizardConfig = getWizardConfiguration(mode, translate);

    // Helper function to find driving asset ID (asset immediately above in physical connection)
    const getDrivingAssetId = (currentAssetId, physicalConnectionArray) => {
        const currentIndex = physicalConnectionArray?.findIndex(asset =>
            (asset.assetId || asset.id || asset.assetID)?.toString() === currentAssetId?.toString()
        );

        if (currentIndex <= 0) {
            return null;
        }

        const previousAsset = physicalConnectionArray?.[currentIndex - 1];
        // Unmonitored gearboxes have a temp string ID; the backend expects -1 for them
        if (previousAsset?.isUnmonitoredGearbox) {
            return -1;
        }
        const assetId = Number(previousAsset?.assetId || previousAsset?.id || previousAsset?.assetID);
        return assetId || null;
    };

    // Helper function to find the nearest Motor asset above in the physical connection chain
    const getDrivingMotorId = (currentAssetId, physicalConnectionArray) => {
        const currentIndex = physicalConnectionArray?.findIndex(asset =>
            (asset.assetId || asset.id || asset.assetID)?.toString() === currentAssetId?.toString()
        );

        if (currentIndex <= 0) {
            return null;
        }

        for (let i = currentIndex - 1; i >= 0; i--) {
            const asset = physicalConnectionArray?.[i];
            const assetType = (asset?.type || asset?.assetType || asset?.originalAsset?.assetType || '').toLowerCase();

            if (assetType === ASSET_TYPES.MOTOR) {
                const motorId = Number(asset?.assetId || asset?.id || asset?.assetID);
                return motorId || null;
            }
        }

        return null;
    };

    // Build individual connected asset for the payload
    const buildConnectedAsset = (asset, physicalConnectionArray, ratios) => {
        const assetId = asset.assetId || asset.id || asset.assetID;
        const assetIdStr = assetId?.toString();
        const isUnmonitored = !!asset.isUnmonitoredGearbox;

        const entry = {
            assetId: isUnmonitored ? -1 : Number(assetId),
            assetType: asset.assetType || asset.type || asset.originalAsset?.assetType || 'GenericMachine',
            typeConfigId: isUnmonitored ? UNMONITORED_GEARBOX_TYPE_CONFIG_ID : asset.typeConfigId || '',
            speedRatio: ratios[assetId] ? Number(ratios[assetId]) : null,
            drivingAssetId: getDrivingAssetId(assetIdStr, physicalConnectionArray),
            drivingMotorId: getDrivingMotorId(assetIdStr, physicalConnectionArray),
            subType: asset.selectedSubType || asset.subType || asset.subTypeName || ''
        };

        if (isUnmonitored) {
            entry.assetName = asset.assetName || asset.name || asset.assetDisplayName;
            entry.isUnmonitoredGearbox = true;
            if (asset.gearboxMetadata?.organization) {
                entry.organizationId = asset.gearboxMetadata.organization?.organizationID ||
                    asset.gearboxMetadata.organization?.id;
            }
            if (asset.gearboxMetadata?.site) {
                entry.gearboxSiteId = asset.gearboxMetadata.site?.siteId ||
                    asset.gearboxMetadata.site?.id;
            }
            if (asset.gearboxMetadata?.assetAccessGroup) {
                entry.assetAccessGroup = asset.gearboxMetadata.assetAccessGroup;
            }
        }

        return entry;
    };

    // Track validation state for BasicInfoStep (Step 0)
    const [isBasicInfoValid, setIsBasicInfoValid] = useState(false);
    const [hasBasicInfoInteraction, setHasBasicInfoInteraction] = useState(false);

    const {
        currentStep,
        currentStepIndex,
        formData,
        canGoNext,
        canGoPrevious,
        isLastStep,
        isFirstStep,
        goToStep,
        nextStep,
        previousStep,
        updateStepData,
        resetWizard
    } = useWizardFlow({ mode, model: existingModel, actions, translate });

    const handleStepComplete = async (stepData) => {
        updateStepData(currentStep?.key, stepData);

        if (isLastStep) {
            // Create updated form data with the final step data
            const updatedFormData = {
                ...formData,
                [currentStep?.key]: {
                    ...formData[currentStep?.key],
                    ...stepData
                }
            };

            onComplete(updatedFormData);
        } else {
            nextStep();
        }
    };

    const handleNext = () => {

        if (isLastStep) {

            // For the last step (PreviewStep), we need to transform the data to API format
            // Let's replicate the PreviewStep's transformToApiPayload logic
            const basicInfo = formData.basicInfo || {};
            const assetSelection = formData.assetSelection || {};
            const physicalConnection = formData.physicalConnection || {};


            const selectedAssets = assetSelection.selectedAssets || assetSelection.addedAssets || [];
            // Ensure physicalConnectionAssets is always an array
            const physicalConnectionData = physicalConnection.physicalConnection || physicalConnection.connections || [];
            const physicalConnectionAssets = Array.isArray(physicalConnectionData) ? physicalConnectionData : [];
            const outputRatios = physicalConnection.outputRatios || {};

            // Separate connected and not connected assets based on physical connection.
            // Only use real assetIds (not temp IDs from unmonitored gearboxes) for the lookup.
            const connectedAssetIds = new Set(
                physicalConnectionAssets
                    .filter(asset => !asset.isUnmonitoredGearbox)
                    .map(asset => (asset.assetId || asset.id || asset.assetID)?.toString())
                    .filter(Boolean)
            );

            // Regular assets from Step 2 that are in physical connection
            const regularConnectedAssets = selectedAssets?.filter(asset => {
                const assetIdStr = (asset.assetId || asset.id || asset.assetID)?.toString();
                return assetIdStr && connectedAssetIds.has(assetIdStr);
            }) || [];

            // Unmonitored gearboxes only exist in physicalConnectionAssets, never in selectedAssets
            const unmonitoredGearboxes = physicalConnectionAssets.filter(asset => asset.isUnmonitoredGearbox);

            const notConnectedAssets = selectedAssets?.filter(asset => {
                const assetIdStr = (asset.assetId || asset.id || asset.assetID)?.toString();
                return !assetIdStr || !connectedAssetIds?.has(assetIdStr);
            }) || [];

            // Create API payload in the format expected by the API
            const apiPayload = {
                name: basicInfo.powertrainName || basicInfo.name || '',
                siteId: basicInfo.selectedSite?.siteId || basicInfo.selectedSite?.id || null,
                locationId: basicInfo.selectedLocation?.locationId || basicInfo.selectedLocation?.id || null,
                notConnectedAssets: notConnectedAssets.map(asset => ({
                    assetId: Number(asset.assetId || asset.id || asset.assetID),
                    assetType: asset.assetType || asset.type || asset.assetTypeName,
                    typeConfigId: asset.typeConfigId || '',
                    drivingAssetId: null,
                    drivingMotorId: null,
                    subType: asset.selectedSubType || asset.subType || asset.subTypeName || ''
                })),
                connectedAssets: [
                    ...regularConnectedAssets.map(asset =>
                        buildConnectedAsset(asset, physicalConnectionAssets, outputRatios)
                    ),
                    ...unmonitoredGearboxes.map(asset =>
                        buildConnectedAsset(asset, physicalConnectionAssets, outputRatios)
                    )
                ]
            };

            // Add edit mode specific fields
            if (mode === 'edit') {
                apiPayload.powertrainId = basicInfo?.powertrainId || '';
                apiPayload.rowVersion = basicInfo?.rowVersion || '';
            }

            // Call handleStepComplete with the API payload
            handleStepComplete(apiPayload);
        } else {
            nextStep();
        }
    };

    const handleCancel = () => {
        resetWizard();
        onCancel();
    };

    const handleSaveFromHeader = () => {
        // In edit mode, save the current wizard data without going to the last step
        // This allows users to save changes from any step
        if (mode === 'edit') {

            // Transform formData to API payload format (same as in handleNext for last step)
            const basicInfo = formData.basicInfo || {};
            const assetSelection = formData.assetSelection || {};
            const physicalConnection = formData.physicalConnection || {};

            const selectedAssets = assetSelection.selectedAssets || assetSelection.addedAssets || [];
            // Ensure physicalConnectionAssets is always an array
            const physicalConnectionData = physicalConnection.physicalConnection || physicalConnection.connections || [];
            const physicalConnectionAssets = Array.isArray(physicalConnectionData) ? physicalConnectionData : [];
            const outputRatios = physicalConnection.outputRatios || {};

            // Separate connected and not connected assets.
            // Exclude temp IDs from unmonitored gearboxes when building the lookup set.
            const connectedAssetIds = new Set(
                physicalConnectionAssets
                    .filter(asset => !asset.isUnmonitoredGearbox)
                    .map(asset => (asset.assetId || asset.id || asset.assetID)?.toString())
                    .filter(Boolean)
            );

            const regularConnectedAssets = selectedAssets?.filter(asset => {
                const assetIdStr = (asset.assetId || asset.id || asset.assetID)?.toString();
                return assetIdStr && connectedAssetIds?.has(assetIdStr);
            });

            const unmonitoredGearboxes = physicalConnectionAssets?.filter(asset => asset.isUnmonitoredGearbox);

            const notConnectedAssets = selectedAssets?.filter(asset => {
                const assetIdStr = (asset.assetId || asset.id || asset.assetID)?.toString();
                return !assetIdStr || !connectedAssetIds?.has(assetIdStr);
            });

            // Create API payload
            const apiPayload = {
                powertrainId: basicInfo.powertrainId || basicInfo.id || 0,
                name: basicInfo.powertrainName || basicInfo.name || '',
                siteId: basicInfo.selectedSite?.siteId || basicInfo.selectedSite?.id || null,
                locationId: basicInfo.selectedLocation?.locationId || basicInfo.selectedLocation?.id || null,
                notConnectedAssets: notConnectedAssets.map(asset => ({
                    assetId: Number(asset.assetId || asset.id || asset.assetID),
                    assetType: asset.assetType || asset.type || asset.assetTypeName,
                    typeConfigId: asset.typeConfigId || '',
                    drivingAssetId: null,
                    drivingMotorId: null,
                    subType: asset.selectedSubType || asset.subType || asset.subTypeName || ''
                })),
                connectedAssets: [
                    ...regularConnectedAssets.map(asset =>
                        buildConnectedAsset(asset, physicalConnectionAssets, outputRatios)
                    ),
                    ...unmonitoredGearboxes.map(asset =>
                        buildConnectedAsset(asset, physicalConnectionAssets, outputRatios)
                    )
                ],
                rowVersion: basicInfo?.rowVersion || ''
            };

            // Call onComplete with API payload
            onComplete(apiPayload);
        }
    };

    const handleReset = () => {
        if (window.confirm('Are you sure you want to reset the wizard? All progress will be lost.')) {
            resetWizard();
        }
    };

    // Determine which steps have errors for stepper visualization
    const errorSteps = useMemo(() => {
        const errors = [];

        // Check BasicInfoStep (STEP1, index 0) validation
        // Only show error indicator after user has interacted with the form
        if (!isBasicInfoValid && hasBasicInfoInteraction && currentStepIndex >= 0) {
            errors.push(0);
        }

        // Check if Step 2 (Asset Selection) has changes that affect physical connection
        const assetSelectionData = formData.assetSelection;
        if (mode === 'edit' && assetSelectionData?.hasPhysicalConnectionChanges) {
            // Mark Step 3 (Physical Connection) as having errors when assets are modified in Step 2
            errors.push(2);
        }

        // Check Physical Connection step (STEP3, index 2) for validation errors
        // Run validation when on step 2 or beyond (if data exists), or in edit mode with pending changes
        const physicalConnectionData = formData.physicalConnection;
        const shouldValidatePhysicalConnection = currentStepIndex >= 2 ||
            mode === 'edit' && currentStepIndex > 2 && assetSelectionData?.hasPhysicalConnectionChanges;

        if (physicalConnectionData && shouldValidatePhysicalConnection) {
            // Skip validation if step was skipped
            if (physicalConnectionData.isSkipped) {
                return errors; // No errors if step was skipped
            }

            // Get the actual physical connection assets and data
            const physicalConnectionAssets = physicalConnectionData.physicalConnection || [];
            const selectedAssets = physicalConnectionData.selectedAssets || [];
            const outputRatios = physicalConnectionData.outputRatios || {};

            // Check if there's only 1 asset in physical connection - this is always an error
            if (physicalConnectionAssets.length === 1) {
                if (!errors.includes(2)) {
                    // For stpper and save button error / only one asset in physical connection
                    errors.push(2);
                }
            }

            // Only validate if we have assets in physical connection and asset details
            if (physicalConnectionAssets?.length > 0 && assetDetails?.length > 0) {
                try {
                    const validationResult = validatePhysicalConnection(
                        physicalConnectionAssets,
                        assetDetails, // Pass the array directly, not as a map
                        outputRatios,
                        translate
                    );

                    // Mark as error if there are errors, warnings, or validation is not valid
                    // This blocks user from proceeding until all issues are resolved
                    const hasIssues = validationResult?.hasErrors ||
                        validationResult?.hasWarnings ||
                        !validationResult?.isValid;

                    if (hasIssues && !errors.includes(2)) {
                        // For stpper and save button error/ morethan one asset in physical connection
                        errors.push(2); // Physical Connection is step index 2 (STEP3)
                    }
                } catch (error) {
                    // validation errors are handled via return value; ignore exceptions
                }
            } else if (selectedAssets?.length > 0 && physicalConnectionAssets?.length === 0) {
                // If we have selected assets but no physical connections, this could be an error
                // unless all assets are non-driving assets that don't require connections
                if (assetDetails.length > 0) {
                    try {
                        // Validate even with empty connections to check for missing required connections
                        const validationResult = validatePhysicalConnection(
                            [],
                            assetDetails, // Pass the array directly
                            outputRatios,
                            translate
                        );

                        // Mark as error if there are errors, warnings, or validation is not valid
                        const hasIssues = validationResult?.hasErrors ||
                            validationResult?.hasWarnings ||
                            !validationResult?.isValid;
                        if (hasIssues && !errors.includes(2)) {
                            // For stpper, Next and save button error/ no assets in physical connection but selected assets exist
                            errors.push(2);
                        }
                    } catch (error) {
                        // validation errors are handled via return value; ignore exceptions
                    }
                }
            }
        }

        // Clear hasPhysicalConnectionChanges flag if Step 3 validation passes
        // This flag is set when assets are changed in Step 2 during edit mode
        // Once the user visits Step 3 and validation passes, clear the flag
        if (mode === 'edit' &&
            assetSelectionData?.hasPhysicalConnectionChanges &&
            !errors.includes(2) &&
            currentStepIndex >= 2) {
            // Clear the flag since errors are resolved
            setTimeout(() => {
                updateStepData('assetSelection', {
                    ...assetSelectionData,
                    hasPhysicalConnectionChanges: false
                });
            }, 0);
        }

        return errors;
    }, [formData?.physicalConnection, formData?.physicalConnection?.outputRatios, formData?.assetSelection, assetDetails, currentStepIndex, mode, updateStepData, isBasicInfoValid]);    // Override canGoNext to disable when current step has validation errors

    // Comprehensive validation for saving/creating a powertrain. Computed directly from formData
    // so it is reliable from any step (the edit-mode header Save button works from any step).
    const canSavePowertrain = useMemo(() => {
        const basicInfo = formData?.basicInfo || {};
        const powertrainName = (basicInfo.powertrainName || basicInfo.name || '').trim();
        const hasName = powertrainName?.length > 0;
        const hasSite = !!(basicInfo?.selectedSite?.siteId ?? basicInfo?.selectedSite?.id);
        const hasAssets = (formData?.assetSelection?.selectedAssets?.length || 0) > 0;
        const hasPhysicalConnectionErrors = errorSteps.includes(2);

        // step1: name mandatory, step2: at least one asset, step3: no physical connection errors,
        // step4: name + at least one asset (single or in physical connection).
        return hasName && hasSite && hasAssets && isBasicInfoValid && !hasPhysicalConnectionErrors;
    }, [formData?.basicInfo, formData?.assetSelection?.selectedAssets, errorSteps, isBasicInfoValid]);

    const canGoNextWithValidation = useMemo(() => {
        // If current step is BasicInfo (index 0) and has validation errors, prevent next
        if (currentStepIndex === 0 && !isBasicInfoValid) {
            return false;
        }
        if (currentStepIndex === 1 && formData?.assetSelection?.selectedAssets?.length === 0) {
            return false;
        }

        // If current step is Physical Connection (index 2), delegate to helper
        if (currentStepIndex === 2) {
            return canProceedFromPhysicalConnection(formData, assetDetails, errorSteps, translate);
        }

        // If on Preview step (index 3), block Create/Update unless the powertrain is fully valid:
        // name present, at least one asset, and no physical connection errors.
        if (currentStepIndex === 3) {
            return canSavePowertrain;
        }

        return canGoNext;
    }, [canGoNext, canSavePowertrain, currentStepIndex, errorSteps, formData.physicalConnection, formData.assetSelection, isBasicInfoValid]);

    // Compute which steps should be disabled for stepper click navigation
    const disabledSteps = useMemo(() => {
        const disabled = [];
        const hasValidName = isBasicInfoValid;
        const hasAssets = formData?.assetSelection?.selectedAssets?.length > 0;
        const hasPhysicalConnectionErrors = errorSteps.includes(2);

        // Step 1 (Basic Info) is always accessible
        // Step 2+ requires valid basic info (powertrain name)
        if (!hasValidName) {
            disabled.push(1, 2, 3);
            return disabled;
        }
        // Step 3+ requires at least one asset selected
        if (!hasAssets) {
            disabled.push(2, 3);
            return disabled;
        }
        // Step 4 requires no physical connection errors (unless skipped)
        if (hasPhysicalConnectionErrors) {
            disabled.push(3);
        }
        return disabled;
    }, [isBasicInfoValid, formData?.assetSelection?.selectedAssets, errorSteps]);

    // Validated step click handler - prevents navigation to disabled steps
    const handleStepClick = (stepIndex) => {
        if (disabledSteps.includes(stepIndex)) {
            return;
        }
        goToStep(stepIndex);
    };

    return (
        <div className={`wizard-container ${className}`}>
            {/* Loading overlay for powertrain operations */}
            {(isAddingPowertrain || isEditingPowertrain) &&
                <div className='wizard-loading-overlay'>
                    <div className='wizard-loading-content'>
                        <LoadingIndicator size='large' color='#0078D4' />
                        <div className='wizard-loading-text'>
                            {isAddingPowertrain ?
                                translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Preview_CreatingPowertrainMessage') :
                                translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Preview_UpdatingPowertrainMessage')
                            }
                        </div>
                    </div>
                </div>
            }

            <div className='wizard-header'>
                <div className='wizard-header-left'>
                    <h2 className='wizard-title'>
                        {mode === 'edit' ?
                            translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_EditPowertrainTitle') :
                            translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_CreatePowertrainTitle')
                        }
                    </h2>
                </div>
                {mode === 'edit' &&
                    <div className='wizard-header-actions'>
                        <GeneralButton
                            type='normal'
                            text={translate('ABB.Powertrain.Frontend.cancelLabel')}
                            onClick={handleCancel}
                            disabled={isAddingPowertrain || isEditingPowertrain}
                        />
                        <GeneralButton
                            type='primary'
                            text={isEditingPowertrain ?
                                translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_UpdatingPowertrainText') :
                                translate('ABB.Powertrain.Frontend.saveButton')
                            }
                            onClick={handleSaveFromHeader}
                            disabled={isAddingPowertrain || isEditingPowertrain || !canSavePowertrain}
                        />
                    </div>
                }
            </div>

            <WizardStepper
                steps={wizardConfig.steps}
                currentStepIndex={currentStepIndex}
                onStepClick={handleStepClick}
                orientation='horizontal'
                showStepNumbers={true}
                showConnectors={true}
                errorSteps={errorSteps}
                disabledSteps={disabledSteps}
            />

            <div className='wizard-content'>
                <WizardStep
                    step={currentStep}
                    stepData={formData[currentStep?.key] || {}}
                    allStepData={formData}
                    onStepDataChange={(data) => {
                        updateStepData(currentStep?.key, data);
                    }}
                    onStepComplete={handleStepComplete}
                    onValidationChange={(isValid, hasInteraction) => {
                        // Update validation state only for BasicInfoStep
                        if (currentStep?.key === 'basicInfo') {
                            setIsBasicInfoValid(isValid);
                            // Track if user has interacted with the form
                            if (hasInteraction) {
                                setHasBasicInfoInteraction(true);
                            }
                        }
                    }}
                    isActive={true}
                    mode={mode}
                    sites={sites}
                    actions={actions}
                    // Pass Redux state for assets
                    availableAssets={availableAssets}
                    isLoadingAssets={isLoadingAssets}
                    assetsError={assetsError}
                    // Pass Redux state for asset subtypes
                    assetSubTypes={assetSubTypes}
                    isLoadingAssetSubTypes={isLoadingAssetSubTypes}
                    assetSubTypesError={assetSubTypesError}
                    // Pass Redux state for asset details
                    assetDetails={assetDetails}
                    isLoadingAssetDetails={isLoadingAssetDetails}
                    assetDetailsError={assetDetailsError}
                    // Pass Redux state for powertrain operations
                    isAddingPowertrain={isAddingPowertrain}
                    isEditingPowertrain={isEditingPowertrain}
                    powertrainOperationError={powertrainOperationError}
                    hierarchyData={hierarchyData}
                    organizationId={organizationId}
                    preSelectedAssetIds={preSelectedAssetIds}
                />
            </div>

            <WizardActions
                canGoNext={canGoNextWithValidation}
                canGoPrevious={canGoPrevious}
                canSave={canSavePowertrain}
                isFirstStep={isFirstStep}
                isLastStep={isLastStep}
                isLoading={isAddingPowertrain || isEditingPowertrain}
                onNext={handleNext}
                onPrevious={previousStep}
                onCancel={handleCancel}
                onReset={handleReset}
                submitLabel={mode === 'edit' ?
                    translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_UpdatePowertrainButton') :
                    translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_CreatePowertrainButton')
                }
                showReset={wizardConfig.navigation.showResetButton}
            />

            {showDebug &&
                <div className='wizard-debug'>
                    <h4>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_DebugTitle')}</h4>
                    <div className='debug-info'>
                        <div className='debug-row'>
                            <strong>Mode:</strong> {mode}
                        </div>
                        <div className='debug-row'>
                            <strong>Current Step:</strong> {currentStep?.id} - {currentStep?.title}
                        </div>
                        <div className='debug-row'>
                            <strong>Step Index:</strong> {currentStepIndex}
                        </div>
                        <div className='debug-row'>
                            <strong>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_CanNavigateLabel')}</strong> {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_PreviousLabel')} {canGoPrevious ? translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_YesLabel') : translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_NoLabel')}, {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_NextLabel')} {canGoNext ? translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_YesLabel') : translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_NoLabel')}
                        </div>
                        <div className='debug-row'>
                            <strong>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_StepFlagsLabel')}</strong> {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_FirstLabel')} {isFirstStep ? translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_YesLabel') : translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_NoLabel')}, {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_LastLabel')} {isLastStep ? translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_YesLabel') : translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_WizardContainer_NoLabel')}
                        </div>
                    </div>
                    <details className='debug-details'>
                        <summary>Form Data</summary>
                        <pre className='debug-json'>
                            {JSON.stringify(formData, null, 2)}
                        </pre>
                    </details>
                    <details className='debug-details'>
                        <summary>Current Step Config</summary>
                        <pre className='debug-json'>
                            {JSON.stringify(currentStep, null, 2)}
                        </pre>
                    </details>
                </div>
            }
        </div>
    );
};

WizardContainer.propTypes = {
    mode: PropTypes.oneOf(['create', 'edit']),
    existingModel: PropTypes.object,
    onComplete: PropTypes.func,
    onCancel: PropTypes.func,
    actions: PropTypes.object,
    sites: PropTypes.array,
    className: PropTypes.string,
    showDebug: PropTypes.bool,
    // Redux state for assets
    availableAssets: PropTypes.array,
    isLoadingAssets: PropTypes.bool,
    assetsError: PropTypes.object,
    // Redux state for asset subtypes
    assetSubTypes: PropTypes.array,
    isLoadingAssetSubTypes: PropTypes.bool,
    assetSubTypesError: PropTypes.object,
    // Redux state for asset details
    assetDetails: PropTypes.array,
    isLoadingAssetDetails: PropTypes.bool,
    assetDetailsError: PropTypes.object,
    // Redux state for powertrain operations
    isAddingPowertrain: PropTypes.bool,
    isEditingPowertrain: PropTypes.bool,
    powertrainOperationError: PropTypes.object,
    hierarchyData: PropTypes.object,
    organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    preSelectedAssetIds: PropTypes.array
};

export default WizardContainer;
